import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Table, Input, Button, Row, Col, Label } from 'reactstrap';
import SweetAlert from 'react-bootstrap-sweetalert';
import moment from "moment";

// Include Translation tool
import { useTranslate } from "react-translate";

// Include a particular scss for this view
import 'assets/scss/custom/_kilab_list.scss';
import { useDispatch, useSelector } from 'react-redux';
import { actionSweetAlert } from 'store/actions/commonActions';
import LoadingLocal from 'plugins/TS-lib-utils-public/components/Loading/LoadingLocal';

//Include store actions
import { 
    actionSetLabContext,
    actionSendLabContext,
    actionGenerateAILabComment
} from "store/actions/laboratoryActions";

import { 
    actionSetContext, 
    actionSendContext, 
    actionGenerateAIComment, 
    actionSetAIGenerationError 
} from 'store/actions/kiLabActions';

const LaboratoryContext = ({
    view,
    setActiveTab,
    tabId,
    setMasterEdited,
    setCanSend,
    canVote,
}) => {

    const dispatch = useDispatch();
    const tEditor = useTranslate('kilab-editor');
    const tKilab = useTranslate("kilab");
    const tLabDetail = useTranslate("laboratory-detail");
    const thl7 = useTranslate('hl7-status');
    const practiceId = useSelector(state => state.practicesReducer.practice._id);
    const study = useSelector(state => view === "kilab" ? state.kiLabReducer.study : state.laboratoryReducer.study);
    const aiGenError = useSelector(state =>  view === "kilab" ? state.kiLabReducer.aiGenerationError : state.laboratoryReducer.aiGenerationError);
    const isLoadingAI = useSelector((state) => view === "kilab" ? state.kiLabReducer.loadingAiReview : state.laboratoryReducer.loadingAiReview);
    const context = useSelector(state => view === "kilab" ? state.kiLabReducer.context : state.laboratoryReducer.context);
    //const context = view === "kilab" ? useSelector(state => state.kiLabReducer.context) : useSelector(state.laboratoryReducer.context);
    const loaded = useRef(false);

    const user = useSelector(state => state.userReducer);
    const studyId = view === "labor" && study && study.metadata ? study.metadata.adminValues.order_number : study.metadata.analysis_id.ext_ref;
    const studyStatus = view === "labor" && study && study.metadata ? study.metadata.reportStatus : study.status;

    const [bornYear, setBornYear] = useState(Object.keys(context).length ? context.birthYear : Object.keys(study).length ? view === "labor" ? study.patient.birthday.substring(0,4) : study.metadata.patient_data.birth_year : "");
    const [gender, setGender] = useState(Object.keys(context).length ? context.gender : Object.keys(study).length ? view === "labor"  ? study.patient.gender : study.metadata.patient_data.gender : "");
    const [hormonYear, setHormonYear] = useState(context.hormoneYear);
    const [hormonCycle, setHormonCycle] = useState(context.hormoneCycle ? context.hormoneCycle : "");
    const [hormonMonth, setHormonMonth] = useState(context.hormoneMonth ? context.hormoneMonth : "");
    const [hormonStatus, setHormonStatus] = useState(context.hormoneStatus);
    const [additional, setAdditional] = useState(context.additionalInfo);
    const [edited, setEdited] = useState(false);
    const [generated, setGenerated] = useState(false);
    const [mobile, setMobile] = useState(window.innerWidth < 1350);

    //store functions
    const setContext = (ctx) => {
        if (view === "kilab") {
            dispatch(actionSetContext(ctx));
        } else {
            dispatch(actionSetLabContext(ctx));
        }
    }

    const sendContext = (studyId, ctx) => {
        if (view === "kilab") {
            dispatch(actionSendContext(studyId, ctx));
        } else {
            dispatch(actionSendLabContext(studyId, ctx));
        }
    }

    const generateAIComment = (id) => {
        if (view === "kilab") {
            dispatch(actionGenerateAIComment(id));
        } else {
            dispatch(actionGenerateAILabComment(id, practiceId));
        }
    }

    //Function to control resizing side-effects
    const handleWidth = useCallback(() => {
        setMobile(prev => (
            prev !==  window.innerWidth < 1350
            ? window.innerWidth < 1350
            : prev
        ));
    }, [mobile]);

    //Effect to control minor resize details
    useEffect(() => {
        window.addEventListener('resize', handleWidth);
        return () => window.removeEventListener('resize', handleWidth);
    }, []);

    //Effect for general record saving
    useEffect(() => {
        if (Object.keys(study).length && loaded.current) {
            setContext({
                gender: gender,
                birthYear: bornYear,
                hormoneYear: hormonYear,
                hormoneStatus: hormonStatus,
                hormoneCycle: hormonCycle ? hormonCycle : undefined,
                hormoneMonth: hormonMonth ? hormonMonth : undefined,
                additionalInfo: additional
            });

            sendContext(study._id, {
                gender: gender,
                birthYear: bornYear,
                hormoneYear: hormonYear,
                hormoneStatus: hormonStatus,
                hormoneCycle: hormonCycle ? hormonCycle : undefined,
                hormoneMonth: hormonMonth ? hormonMonth : undefined,
                additionalInfo: additional
            });
        }
    }, [bornYear, gender, hormonYear, hormonStatus, additional, hormonMonth, hormonCycle]);

    //Effect for edition
    useEffect(() => {
        if (Object.keys(study).length && loaded.current) {
            setEdited(true);
            setMasterEdited(true);
        }
    }, [bornYear, gender, additional, hormonYear, hormonStatus, hormonMonth, hormonCycle]);


    //Set context locally on comment edition
    useEffect(() => {
        setContext({
            gender: gender,
            birthYear: bornYear,
            hormoneYear: hormonYear,
            hormoneStatus: hormonStatus,
            hormoneCycle: hormonCycle ? hormonCycle : undefined,
            hormoneMonth: hormonMonth ? hormonMonth : undefined,
            additionalInfo: additional
        });
    }, [edited]);

    useEffect(() => {
        loaded.current = true;
    }, [context]);

    //Effect in case of AI generation error
    useEffect(() => {
        if (aiGenError && view === "kilab") {
            setGenerated(false);
            dispatch(
                  actionSweetAlert(
                    <SweetAlert
                      custom
                      customIcon="/General/icons/warning-sign-orange.svg"
                      title={tKilab('ai-generation-error-title')}
                      onConfirm={() => {}}
                      customButtons={
                        <>
                          {/*Custom buttons for more control*/}
                          <Button
                            className="mb-3"
                            color="info"
                            size="lg"
                            onClick={() => {
                              dispatch(actionSweetAlert(null));
                              dispatch(actionSetAIGenerationError(false));
                            }}
                          >
                            {tKilab('btn-ok')}
                          </Button>
                        </>
                      }
                    >
                        {tKilab("ai-generation-error")}
                    </SweetAlert>
                  )
                );
        }
    }, [aiGenError])
    
    

    //Hardcoded values
    const femaleHormonStatus = [
        { state:"Zyklustag", value: "cycle"},
        { state:"Schwangerschaft", value: "pregnancy"},
        { state:"Menarche", value: "menarche"},
        { state:"Prämenopause", value: "premenopause"},
        { state:"Perimenopause", value: "perimenopause"},
        { state:"Menopause", value: "Menopause"},
        { state:"Postmenopause", value: "Postmenopause"},
    ];

    const maleHormonStatus = [
        { state:"Vorpubertät", value: "prepuberty"},
        { state:"Postpubertät", value: "post-puberty"},
        { state:"Andropause", value: "andropause"},
    ];

    const handleGenerateClick = () => {
        setGenerated(true);
        setActiveTab(tabId);
        generateAIComment(study._id);
        setEdited(false);
        setMasterEdited(true);
        setCanSend(false);
    }
    
    // Genera arrays de años, dias y meses
    const years = [];
    const cycles = [];
    const months = [];
    const currentYear = new Date().getFullYear();

    for (let year = currentYear; year >= 1920; year--) {
        years.push(String(year));
    }

    for (let i = 1; i <= 99; i++) {
        cycles.push(String(i));
    }

    for (let i = 1; i <= 11; i++) {
        months.push(String(i));
    }

    if (!Object.keys(study).length || !study.metadata) {
        return <></>;
    }
 
    return (
        <Row>
            {/* Left side - Details in a table with custom border style */}
            <Col md={6}>
                <Table borderless className="details-table">
                    <tbody>
                        <tr className="table-row">
                            <td><Label className="title">{ mobile ? "ID" : tEditor("id")}</Label></td>
                            <td><Label className="title">{view == "labor" ? tLabDetail("patient") : tEditor("receipt-date")}</Label></td>
                            {study.medicalValues && study.medicalValues.length ? 
                            <td><Label className="title">{tEditor("born-date")}</Label></td> 
                            : <td><Label className="title">{view == "labor" ? tEditor("receipt-date") : tEditor("status")}</Label></td>}
                        </tr>
                        <tr className="table-row-space">
                            <td><p testId="study_id">{studyId}</p></td>
                            <td><p>{view === "labor" ? `${study.patient.firstName} ${study.patient.lastName}` : "22-08-2024"}</p></td>
                            
                            {study.medicalValues && study.medicalValues.length ? (
                                <td>
                                    <Input type="select" disabled={isLoadingAI} name="bornYear" testId="born_year" onChange={e => {setBornYear(e.target.value)}}>
                                        <option key="" value="">{tEditor("choose-year")}</option>
                                        {years.map((year) => (
                                            <option key={year} value={year} selected={bornYear === year}>
                                                {year}
                                            </option>
                                        ))}
                                    </Input>
                                </td>
                            ) : <td>
                                    <p>{view === "labor" ? moment(study.metadata.updated_at, "YYYYMMDDhhmmss").format(user.dateFormat) : "-"}</p>
                                </td>
                            }
                            

                        </tr>

                        {study.medicalValues && study.medicalValues.length ? (<>
                            <tr className="table-row">
                                <td><Label className="title">{tEditor("gender")}</Label></td>
                                <td><Label className="title">{tEditor("hormone-status")}</Label></td>
                                <td><Label className="title">{tEditor("statement")}</Label></td>
                            </tr>
                            <tr className="table-row-space">
                                <td>
                                    <Input type="select" disabled={isLoadingAI} testId="gender_input" onChange={e => {
                                            setHormonStatus("");
                                            setHormonYear("");
                                            setHormonMonth("");
                                            setHormonCycle("");
                                            setGender(e.target.value);
                                        }}>
                                        <option value="">Geschlecht</option>
                                        <option value="Male" testId="male_option" selected={gender === "Male"}>{tEditor("Male")}</option>
                                        <option value="Female" testId="female_option" selected={gender === "Female"}>{tEditor("Female")}</option>
                                    </Input>
                                </td>
                                <td>
                                    <Input type="select" disabled={isLoadingAI} testId="hormonstatus_input" onChange={e => {
                                            setHormonYear("");
                                            setHormonMonth("");
                                            setHormonCycle("");
                                            setHormonStatus(e.target.value)
                                        }}>
                                        <option value="">-</option>
                                        {gender === "Female" ? femaleHormonStatus.map((o, i) => (
                                            <option 
                                                value={o.value} 
                                                testId={o.value}
                                                key={`female-hormon-${i+1}`} 
                                                selected={hormonStatus === o.value}
                                            >
                                                {o.state}
                                            </option>
                                        )) : gender === "Male" ? maleHormonStatus.map((o,i) => (
                                            <option 
                                                value={o.value} 
                                                testId={o.value}
                                                key={`male-hormon-${i+1}`} 
                                                selected={hormonStatus === o.value}>
                                                {o.state}
                                            </option>
                                        )) : (<></>)}
                                    </Input>
                                </td>
                                <td>

                                    {hormonStatus === "cycle" ? (
                                        <Input type="select" disabled={isLoadingAI} name="hormonCycle" testId="hormon_cycle_input" onChange={e => {
                                                setHormonYear("");
                                                setHormonMonth("");
                                                setHormonCycle(e.target.value);
                                            }}>
                                            <option key="" value="">{tEditor("day")}</option>
                                            {cycles.map((cycle) => (
                                                <option key={`cycle-${cycle}`} value={cycle} testId={`hormon_cycle_${cycle}`} selected={hormonCycle === cycle}>{cycle}</option>
                                            ))}
                                        </Input>
                                    ) : hormonStatus === "pregnancy" ? (
                                        
                                        <Input type="select" disabled={isLoadingAI} name="hormonMonth" testId="hormon_month_input" onChange={e => {
                                            setHormonMonth(e.target.value);
                                            setHormonYear("");
                                            setHormonCycle("");
                                        }}>
                                            <option key="" value="">{tEditor("month")}</option>
                                            {months.map((month) => (
                                                <option key={`month-${month}`} value={month} testId={`hormon_month_${month}`} selected={hormonMonth === month}>{month}</option>
                                            ))}
                                        </Input>
                                        ) : (
                                        <Input type="select" disabled={isLoadingAI || (!hormonStatus || !hormonStatus.length)} testId="hormon_year_input" name="hormonYear" onChange={e => {
                                            setHormonYear(e.target.value);
                                            setHormonMonth("");
                                            setHormonCycle("");
                                        }}>
                                            <option key="" value="">{(!hormonStatus || !hormonStatus.length) ? "-" : tEditor("choose-year")}</option>
                                            {years.map((year) => (
                                                <option key={`year-${year}`} value={year} testId={`hormon_year_${year}`} selected={hormonYear === year}>{year}</option>
                                            ))}
                                        </Input>
                                    )}


                                </td>
                            </tr>
                        </>) : <></>}


                        <tr className="table-row">
                            <td><Label className="title">{view == "labor" ? tLabDetail("study-type") : tEditor("practice")}</Label></td>
                            <td><Label className="title">{tEditor("analysis-status")}</Label></td>
                            <td>{study.medicalValues && study.medicalValues.length ? <Label className="title">{view == "labor" ? tEditor("receipt-date") : tEditor("status")}</Label> : <></>}</td>
                        </tr>
                        <tr className="table-row-space">
                            <td>
                                <p>{view == "labor" 
                                ? study.metadata.adminValues.study_type 
                                ? tLabDetail(study.metadata.adminValues.study_type.toLowerCase().replace(" ", '-')) : tLabDetail("unknown")
                                : study.study_data.admin_values.organization}</p>
                            </td>
                            <td>
                                <p>{thl7(studyStatus)}</p>
                            </td>
                            <td>
                                {study.medicalValues && study.medicalValues.length ? (<p>{view === "labor" ? moment(study.metadata.updated_at, "YYYYMMDDhhmmss").format(user.dateFormat) : "-"}</p>) : <></>}
                            </td>
                        </tr>

                    </tbody>
                </Table>
            </Col>

            {/* Right side - Additional Information */}
            <Col md={6} className="freeTextContainer" >
                <Table borderless className="details-table">
                <tbody>
                    <tr>
                        <td className='table-row'>
                            <div>
                                <p className="freeTextTitle">
                                    <b>{tEditor("extra-info")}</b>
                                </p>
                            </div>
                            <Input
                                type="textarea"
                                name="freeTextInput"
                                testId="additional_info_input"
                                value={additional}
                                disabled={isLoadingAI}
                                onChange={e => {setAdditional(e.target.value)}}
                                placeholder={`Freitext hier eingeben, z.B.:\n- Relevante Medikamente\n- Begleiterkrankungen\n- Körpergröße\n- Gewicht`}
                            />

                            <div className="d-flex" >
                                <Button 
                                    className="mt-3 btn-success" 
                                    testId="ai_generate"
                                    disabled={isLoadingAI || canVote} 
                                    onClick={handleGenerateClick}
                                >
                                    {tEditor('btn-ai-analyze')}
                                </Button>

                                {isLoadingAI && (
                                    <div className={view === "labor" ? "ai-loading" : "kilab-ai-loading"} testId="aigen_loading_indicator">
                                        <LoadingLocal/>
                                        KI-Kommentar wird generiert...
                                    </div>
                                )}
                            </div>
                            

                            </td>
                        </tr>
                    </tbody>
                </Table>
            </Col>
        </Row>
    );
}
export default LaboratoryContext;
